import { create } from 'zustand';

interface File {
  path: string;
  content: string;
  language: string;
  isDirty: boolean;
}

/**
 * Reject paths that are empty, absolute, contain `..` segments, or contain
 * control characters. The IDE shows files in a sandboxed lab workspace; users
 * shouldn't be able to create or rename to anything that escapes it. The
 * backend should re-validate, but catching it client-side gives an immediate
 * error and keeps the Zustand store clean.
 */
export class InvalidPathError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidPathError';
  }
}

// Build the control-char detector via charCode comparison rather than a
// regex literal. Earlier versions of this file used a regex with an escape
// sequence that didn't survive encoding round-trips and ended up matching
// the wrong character class entirely. This loop is unambiguous.
function hasControlChar(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    if (s.charCodeAt(i) < 0x20) return true; // C0 control characters
  }
  return false;
}

export function validateFilePath(path: string): string {
  if (typeof path !== 'string') throw new InvalidPathError('Path must be a string');
  const trimmed = path.trim();
  if (!trimmed) throw new InvalidPathError('Path cannot be empty');
  if (trimmed.length > 512) throw new InvalidPathError('Path is too long');
  if (trimmed.startsWith('/')) throw new InvalidPathError('Absolute paths are not allowed');
  if (trimmed.includes('\0')) throw new InvalidPathError('Path contains a null byte');
  if (hasControlChar(trimmed)) {
    throw new InvalidPathError('Path contains control characters');
  }
  // Reject any segment that is exactly ".." to block traversal.
  const segments = trimmed.split('/');
  if (segments.some((seg) => seg === '..' || seg === '.')) {
    throw new InvalidPathError('Path traversal is not allowed');
  }
  // Reject names ending with whitespace or dot (Windows-hostile).
  if (segments.some((seg) => /[ \t.]$/.test(seg) && seg !== '')) {
    throw new InvalidPathError('Path segment ends with a forbidden character');
  }
  return trimmed;
}

interface Hint {
  id: string;
  usageId?: string;
  patternId: string;
  message: string;
  confidence: number;
  suggestedFix?: string;
  examples?: string[];
  learnMoreUrl?: string;
  category: string;
}

interface ErrorInfo {
  message: string;
  line?: number;
  column?: number;
  severity?: 'error' | 'warning' | 'info';
}

interface CursorPosition {
  line: number;
  column: number;
}

interface Selection {
  start: CursorPosition;
  end: CursorPosition;
}

interface RemoteUser {
  userId: string;
  userName: string;
  userEmail: string;
  color: string;
  cursor: CursorPosition;
  selection?: Selection;
}

interface CollaborationSession {
  id: string;
  fileId: string;
  documentVersion: number;
}

interface IDEState {
  files: Map<string, File>;
  activeFile: string | null;
  openFiles: string[];
  environmentId: string | null;
  isTerminalOpen: boolean;
  terminalHeight: number;
  sidebarWidth: number;

  // Hint system state
  isHintPanelOpen: boolean;
  currentHints: Hint[];
  currentError: ErrorInfo | null;
  hintAttemptCount: number;
  isLoadingHints: boolean;

  // Collaboration state
  collaborationEnabled: boolean;
  collaborationSession: CollaborationSession | null;
  collaborationParticipants: Map<string, RemoteUser>;

  setEnvironmentId: (id: string | null) => void;
  openFile: (path: string, content: string, language?: string) => void;
  replaceFiles: (
    files: Array<{ path: string; content: string; language?: string }>,
  ) => void;
  closeFile: (path: string) => void;
  setActiveFile: (path: string | null) => void;
  updateFileContent: (path: string, content: string) => void;
  markFileSaved: (path: string) => void;
  createFile: (path: string) => void;
  deleteFile: (path: string) => void;
  renameFile: (oldPath: string, newPath: string) => void;
  toggleTerminal: () => void;
  setTerminalHeight: (height: number) => void;
  setSidebarWidth: (width: number) => void;

  // Hint system actions
  toggleHintPanel: () => void;
  setHints: (hints: Hint[]) => void;
  setCurrentError: (error: ErrorInfo | null) => void;
  incrementHintAttempt: () => void;
  resetHintAttempt: () => void;
  setLoadingHints: (loading: boolean) => void;
  clearHints: () => void;

  // Collaboration actions
  toggleCollaboration: () => void;
  setCollaborationSession: (session: CollaborationSession | null) => void;
  addCollaborationParticipant: (user: RemoteUser) => void;
  removeCollaborationParticipant: (userId: string) => void;
  updateParticipantCursor: (userId: string, cursor: CursorPosition, selection?: Selection) => void;
  clearCollaboration: () => void;
}

const getLanguageFromPath = (path: string): string => {
  const ext = path.split('.').pop()?.toLowerCase();
  const langMap: Record<string, string> = {
    py: 'python',
    js: 'javascript',
    ts: 'typescript',
    tsx: 'typescriptreact',
    jsx: 'javascriptreact',
    json: 'json',
    md: 'markdown',
    html: 'html',
    css: 'css',
    yaml: 'yaml',
    yml: 'yaml',
    sql: 'sql',
    sh: 'shell',
    bash: 'shell',
    txt: 'plaintext',
  };
  return langMap[ext || ''] || 'plaintext';
};

export const useIDEStore = create<IDEState>((set, get) => ({
  files: new Map(),
  activeFile: null,
  openFiles: [],
  environmentId: null,
  isTerminalOpen: true,
  terminalHeight: 200,
  sidebarWidth: 250,

  // Hint system initial state
  isHintPanelOpen: false,
  currentHints: [],
  currentError: null,
  hintAttemptCount: 0,
  isLoadingHints: false,

  // Collaboration initial state
  collaborationEnabled: false,
  collaborationSession: null,
  collaborationParticipants: new Map(),

  setEnvironmentId: (id) => set({ environmentId: id }),

  openFile: (path, content, language) => {
    // Defense-in-depth: openFile is also called from the recovery flow with
    // server-supplied paths (Redis snapshot of prior session). If that data
    // is ever tampered with or corrupted, we don't want a /etc/passwd-shaped
    // path to land in the editor state. validateFilePath throws InvalidPathError;
    // we swallow it here so a single bad recovered file doesn't crash the
    // whole restore — log and skip.
    let safePath: string;
    try {
      safePath = validateFilePath(path);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('openFile rejected invalid path', { path, err });
      return;
    }
    const files = new Map(get().files);
    const existingFile = files.get(safePath);

    if (!existingFile) {
      files.set(safePath, {
        path: safePath,
        content,
        language: language || getLanguageFromPath(safePath),
        isDirty: false,
      });
    }

    const openFiles = get().openFiles.includes(safePath)
      ? get().openFiles
      : [...get().openFiles, safePath];

    set({ files, openFiles, activeFile: safePath });
  },

  // Route transitions must replace the workspace atomically. Merging via
  // openFile preserves files from the previous lab and deliberately does not
  // overwrite same-path content, which is correct for opening tabs but unsafe
  // when a different lab becomes authoritative.
  replaceFiles: (incomingFiles) => {
    const files = new Map<string, File>();
    for (const incoming of incomingFiles) {
      let safePath: string;
      try {
        safePath = validateFilePath(incoming.path);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('replaceFiles rejected invalid path', {
          path: incoming.path,
          err,
        });
        continue;
      }
      files.set(safePath, {
        path: safePath,
        content: incoming.content,
        language: incoming.language || getLanguageFromPath(safePath),
        isDirty: false,
      });
    }
    const openFiles = Array.from(files.keys());
    set({
      files,
      openFiles,
      activeFile: openFiles[0] ?? null,
    });
  },

  closeFile: (path) => {
    const openFiles = get().openFiles.filter((f) => f !== path);
    const activeFile = get().activeFile === path
      ? openFiles[openFiles.length - 1] || null
      : get().activeFile;

    set({ openFiles, activeFile });
  },

  setActiveFile: (path) => set({ activeFile: path }),

  updateFileContent: (path, content) => {
    const files = new Map(get().files);
    const file = files.get(path);
    if (file) {
      files.set(path, { ...file, content, isDirty: true });
      set({ files });
    }
  },

  markFileSaved: (path) => {
    const files = new Map(get().files);
    const file = files.get(path);
    if (file) {
      files.set(path, { ...file, isDirty: false });
      set({ files });
    }
  },

  createFile: (path) => {
    const safePath = validateFilePath(path);
    const files = new Map(get().files);
    if (files.has(safePath)) {
      throw new InvalidPathError(`A file already exists at ${safePath}`);
    }
    files.set(safePath, {
      path: safePath,
      content: '',
      language: getLanguageFromPath(safePath),
      isDirty: true,
    });

    const openFiles = [...get().openFiles, safePath];
    set({ files, openFiles, activeFile: safePath });
  },

  deleteFile: (path) => {
    const files = new Map(get().files);
    files.delete(path);

    const openFiles = get().openFiles.filter((f) => f !== path);
    const activeFile = get().activeFile === path
      ? openFiles[openFiles.length - 1] || null
      : get().activeFile;

    set({ files, openFiles, activeFile });
  },

  renameFile: (oldPath, newPath) => {
    const safeNew = validateFilePath(newPath);
    const files = new Map(get().files);
    const file = files.get(oldPath);

    if (!file) return;
    if (oldPath === safeNew) return;
    if (files.has(safeNew)) {
      throw new InvalidPathError(`A file already exists at ${safeNew}`);
    }

    files.delete(oldPath);
    files.set(safeNew, {
      ...file,
      path: safeNew,
      language: getLanguageFromPath(safeNew),
      isDirty: true,
    });

    const openFiles = get().openFiles.map((f) => (f === oldPath ? safeNew : f));
    const activeFile = get().activeFile === oldPath ? safeNew : get().activeFile;

    set({ files, openFiles, activeFile });
  },

  toggleTerminal: () => set({ isTerminalOpen: !get().isTerminalOpen }),
  setTerminalHeight: (height) => set({ terminalHeight: height }),
  setSidebarWidth: (width) => set({ sidebarWidth: width }),

  // Hint system actions
  toggleHintPanel: () => set({ isHintPanelOpen: !get().isHintPanelOpen }),
  setHints: (hints) => set({ currentHints: hints }),
  setCurrentError: (error) => set({ currentError: error }),
  incrementHintAttempt: () => set({ hintAttemptCount: get().hintAttemptCount + 1 }),
  resetHintAttempt: () => set({ hintAttemptCount: 0 }),
  setLoadingHints: (loading) => set({ isLoadingHints: loading }),
  clearHints: () => set({ currentHints: [], currentError: null, hintAttemptCount: 0 }),

  // Collaboration actions
  toggleCollaboration: () => set({ collaborationEnabled: !get().collaborationEnabled }),

  setCollaborationSession: (session) => set({ collaborationSession: session }),

  addCollaborationParticipant: (user) => {
    const participants = new Map(get().collaborationParticipants);
    participants.set(user.userId, user);
    set({ collaborationParticipants: participants });
  },

  removeCollaborationParticipant: (userId) => {
    const participants = new Map(get().collaborationParticipants);
    participants.delete(userId);
    set({ collaborationParticipants: participants });
  },

  updateParticipantCursor: (userId, cursor, selection) => {
    const participants = new Map(get().collaborationParticipants);
    const user = participants.get(userId);
    if (user) {
      participants.set(userId, { ...user, cursor, selection });
      set({ collaborationParticipants: participants });
    }
  },

  clearCollaboration: () => set({
    collaborationSession: null,
    collaborationParticipants: new Map(),
  }),
}));
